import { useEffect, useState } from "react";
import { useCustom, useCustomMutation } from "@refinedev/core";

/**
 * De startpagina van een vestiging, als kaart op het vestigingenscherm.
 *
 * <p>Hier staat alleen wat iemand zelf kiest: de link, de hoofdkleur, de begroeting en welke knoppen
 * uit mogen. Naam, adres, telefoon, openingstijden en logo komen van de vestiging zelf, en welke
 * knoppen er kúnnen zijn volgt uit de modules die aanstaan -- dat bepaalt de api, niet dit scherm.
 * Daarom staat de kaart hier en niet in een eigen instellingenscherm: het hoort bij de vestiging,
 * net als het logo eronder.
 *
 * <p>De hoofdkleur is uit de eigen website van de vestiging te halen. Dat leunt op wat er al was
 * voor het eigen thema van de nazorgvragenlijst; het stelt alleen voor, bewaren is een aparte stap.
 */

const API_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? "/api";

/** Alle knoppen die er kunnen zijn, met hun uitleg. Zelfde namen als {@code StartpaginaKnop.Soort}. */
const KNOPPEN: { sleutel: string; label: string }[] = [
  { sleutel: "DIERMATCH", label: "Ons aanbod op DierMatch" },
  { sleutel: "AFSTAND", label: "Een dier aanmelden voor opvang" },
  { sleutel: "PLEEGGEZIN", label: "Pleeggezin" },
  { sleutel: "KLANTPORTAAL", label: "Reserveren en mijn gegevens" },
  { sleutel: "BACKOFFICE", label: "Inloggen medewerkers" },
];

export interface StartpaginaInstelling {
  vestigingId: number;
  vestigingNaam: string;
  slug: string;
  kleurPrimair: string | null;
  welkomKop: string | null;
  welkomTekst: string | null;
  knoppenUit: string[];
  /** Waar de gekopieerde pagina vandaan komt, of null als er nog geen kopie is. */
  kopieBron: string | null;
  /** Wanneer die kopie is opgehaald. */
  kopieOp: string | null;
  ingesteld: boolean;
}

/** De volledige link, zoals een klant hem op papier zet. */
export function volledigeLink(slug: string, origin: string): string {
  return `${origin.replace(/\/+$/, "")}/start/${slug}`;
}

export function StartpaginaKaart({ vestigingId }: { vestigingId: number }) {
  const url = `${API_URL}/startpagina/${vestigingId}`;
  const { data, isLoading, isError, refetch } = useCustom<StartpaginaInstelling>({ url, method: "get" });
  const { mutate } = useCustomMutation();

  const [slug, setSlug] = useState("");
  const [kleur, setKleur] = useState("");
  const [kop, setKop] = useState("");
  const [tekst, setTekst] = useState("");
  const [uit, setUit] = useState<string[]>([]);
  const [bezig, setBezig] = useState(false);
  const [melding, setMelding] = useState<string | null>(null);
  const [fout, setFout] = useState<string | null>(null);

  const instelling = data?.data;

  // De velden volgen de geselecteerde vestiging. Zonder deze stap blijft bij het wisselen van
  // vestiging de link van de vorige in het veld staan, en bewaart iemand hem per ongeluk hierheen.
  useEffect(() => {
    if (!instelling) {
      return;
    }
    setSlug(instelling.slug ?? "");
    setKleur(instelling.kleurPrimair ?? "");
    setKop(instelling.welkomKop ?? "");
    setTekst(instelling.welkomTekst ?? "");
    setUit(instelling.knoppenUit ?? []);
    setMelding(null);
    setFout(null);
  }, [instelling]);

  const origin = globalThis.location?.origin ?? "";

  function bewaar() {
    setBezig(true);
    setMelding(null);
    setFout(null);
    mutate(
      {
        url,
        method: "put",
        values: {
          slug,
          kleurPrimair: kleur || null,
          welkomKop: kop || null,
          welkomTekst: tekst || null,
          knoppenUit: uit,
        },
      },
      {
        onSuccess: () => {
          setBezig(false);
          setMelding("Bewaard.");
          void refetch();
        },
        onError: () => {
          setBezig(false);
          setFout("Bewaren is niet gelukt.");
        },
      },
    );
  }

  function haalSiteOp() {
    setBezig(true);
    setMelding(null);
    setFout(null);
    mutate(
      { url: `${url}/site-ophalen`, method: "post", values: {} },
      {
        onSuccess: (res) => {
          setBezig(false);
          const k = res?.data as { bron?: string; grootte?: number } | undefined;
          setMelding(
            k?.bron
              ? `De site is opgehaald van ${k.bron}.`
              : "De site is opgehaald.",
          );
          void refetch();
        },
        onError: (err) => {
          setBezig(false);
          // De api geeft een bruikbare melding terug als het adres of de site niet deugt; die
          // is hier nuttiger dan "er ging iets mis".
          const body = (err as { body?: { melding?: string; detail?: string } }).body;
          setFout(body?.melding ?? body?.detail ?? "De site kon niet opgehaald worden.");
        },
      },
    );
  }

  function kleurUitWebsite() {
    setBezig(true);
    setMelding(null);
    setFout(null);
    mutate(
      { url: `${url}/kleur-uit-website`, method: "post", values: {} },
      {
        onSuccess: (res) => {
          setBezig(false);
          const voorgesteld = (res?.data as { kleurPrimair?: string } | undefined)?.kleurPrimair;
          if (voorgesteld) {
            setKleur(voorgesteld);
            setMelding(`Kleur ${voorgesteld} gevonden. Nog niet bewaard.`);
          } else {
            // Geen kleur is geen storing: een klant zonder website hoort geen foutmelding te zien.
            setMelding("Geen kleur uit de website te halen. Vul er zelf een in.");
          }
        },
        onError: () => {
          setBezig(false);
          setFout("De website kon niet opgehaald worden.");
        },
      },
    );
  }

  function wissel(sleutel: string) {
    setUit((huidig) =>
      huidig.includes(sleutel) ? huidig.filter((s) => s !== sleutel) : [...huidig, sleutel],
    );
  }

  if (isLoading) {
    return (
      <section className="vb-card" aria-busy="true">
        <h3 className="vb-card-title">Startpagina</h3>
      </section>
    );
  }

  if (isError || !instelling) {
    return (
      <section className="vb-card">
        <h3 className="vb-card-title">Startpagina</h3>
        <p className="vb-card-note">De instellingen konden niet opgehaald worden.</p>
      </section>
    );
  }

  const link = volledigeLink(slug, origin);

  return (
    <section className="vb-card">
      <div className="vb-card-head">
        <h3 className="vb-card-title">Startpagina</h3>
        {instelling.ingesteld && (
          <a className="vb-card-action" href={`/start/${slug}`} rel="noopener">
            Bekijken
          </a>
        )}
      </div>

      <p className="vb-card-note">
        Eén link die deze locatie aan haar bezoekers geeft. De pagina is een kopie van de eigen
        site van deze vestiging, met de knoppen naar het aanbod, het aanmelden van een dier, het
        klantportaal en de inlog voor medewerkers erin.
      </p>

      <div className="sp-kopie">
        <div>
          {instelling.kopieBron ? (
            <p className="vb-card-note">
              Gekopieerd van <span className="sp-link">{instelling.kopieBron}</span>
              {instelling.kopieOp ? ` op ${new Date(instelling.kopieOp).toLocaleString("nl-NL")}` : ""}.
            </p>
          ) : (
            <p className="vb-card-note">
              Nog geen kopie. Haal de site op om de startpagina te maken.
            </p>
          )}
        </div>
        <button type="button" className="sp-bewaar" disabled={bezig} onClick={haalSiteOp}>
          {bezig ? "Bezig…" : instelling.kopieBron ? "Site opnieuw ophalen" : "Site ophalen"}
        </button>
      </div>

      <div className="sp-veld">
        <label className="sp-label" htmlFor={`sp-slug-${vestigingId}`}>
          Link
        </label>
        <input
          id={`sp-slug-${vestigingId}`}
          className="sp-invoer"
          value={slug}
          onChange={(e) => setSlug(e.target.value)}
          placeholder="bijvoorbeeld: enschede"
        />
        <p className="vb-card-note sp-link">{link}</p>
      </div>

      <div className="sp-veld">
        <label className="sp-label" htmlFor={`sp-kleur-${vestigingId}`}>
          Hoofdkleur
        </label>
        <div className="sp-kleur-rij">
          <input
            id={`sp-kleur-${vestigingId}`}
            className="sp-invoer sp-invoer-kort"
            value={kleur}
            onChange={(e) => setKleur(e.target.value)}
            placeholder="#1b6b4a"
          />
          {/* Een voorbeeldje van de kleur zelf: een hexcode zegt niemand iets. */}
          <span
            className="sp-kleur-blok"
            style={kleur ? { background: kleur } : undefined}
            aria-hidden="true"
          />
          <button type="button" className="vb-card-action" disabled={bezig} onClick={kleurUitWebsite}>
            Uit de website halen
          </button>
        </div>
      </div>

      <div className="sp-veld">
        <label className="sp-label" htmlFor={`sp-kop-${vestigingId}`}>
          Kop
        </label>
        <input
          id={`sp-kop-${vestigingId}`}
          className="sp-invoer"
          value={kop}
          onChange={(e) => setKop(e.target.value)}
          placeholder={instelling.vestigingNaam}
        />
      </div>

      <div className="sp-veld">
        <label className="sp-label" htmlFor={`sp-tekst-${vestigingId}`}>
          Welkomtekst
        </label>
        <input
          id={`sp-tekst-${vestigingId}`}
          className="sp-invoer"
          value={tekst}
          onChange={(e) => setTekst(e.target.value)}
          placeholder="Waar kunnen wij u mee helpen?"
        />
      </div>

      <fieldset className="sp-knoppen">
        <legend className="sp-label">Knoppen</legend>
        <p className="vb-card-note">
          Een knop staat er alleen als hij ergens op uitkomt: dat hangt af van de modules die aanstaan
          en van de koppeling met het platform. Hier zet u er eventueel een uit.
        </p>
        {KNOPPEN.map((knop) => (
          <label className="sp-keuze" key={knop.sleutel}>
            <input
              type="checkbox"
              checked={!uit.includes(knop.sleutel)}
              onChange={() => wissel(knop.sleutel)}
            />
            {knop.label}
          </label>
        ))}
      </fieldset>

      <div className="sp-acties">
        <button type="button" className="sp-bewaar" disabled={bezig} onClick={bewaar}>
          {bezig ? "Bezig…" : instelling.ingesteld ? "Bewaren" : "Startpagina aanzetten"}
        </button>
        {melding && <span className="vb-card-note">{melding}</span>}
        {fout && <span className="sp-fout">{fout}</span>}
      </div>
    </section>
  );
}
